import { fetchAPI } from '@quickbit/backoffice-core-frontend';
import { MoneyScaled } from '@quickbit/qb-design-system';
import {
  useQuery,
  keepPreviousData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

const queryKeys = {
  all: () => ['direct-onramp'] as const,
  search: (searchBody: string) =>
    [...queryKeys.all(), 'search', searchBody] as const,
  allDetails: () => [...queryKeys.all(), 'allDetails'] as const,
  details: (referenceId: string) =>
    [...queryKeys.allDetails(), referenceId] as const,
  pagoraQuickbit: (searchBody: string) =>
    [...queryKeys.all(), 'pagora-quickbit', searchBody] as const,
  restart: (referenceId: string) =>
    [...queryKeys.all(), 'restart', referenceId] as const,
  hotWalletBalances: () => [...queryKeys.all(), 'hotwallet-balances'] as const,
  startDirectOnrampFlow: () =>
    [...queryKeys.all(), 'start-direct-onramp-flow'] as const,
  searchUsers: (searchBody: string) =>
    [...queryKeys.all(), 'search-users', searchBody] as const,
  detailsUsers: (userId: string) =>
    [...queryKeys.all(), 'details-users', userId] as const,
  updateUserStatus: (userId: string, status: CustodianUserStatus) =>
    [...queryKeys.all(), 'update-user-status', userId, status] as const,
};

export enum TransactionRequestState {
  REQUEST_CREATED = 'REQUEST_CREATED',
  CUSTODIAN_USER_CREATED = 'CUSTODIAN_USER_CREATED',
  CUSTODIAN_WALLET_CREATED = 'CUSTODIAN_WALLET_CREATED',
  IDENTITY_IMAGES_SAVED = 'IDENTITY_IMAGES_SAVED',
  BROKER_USER_CREATED = 'BROKER_USER_CREATED',
  BROKER_USER_ACCOUNT_CREATED = 'BROKER_USER_ACCOUNT_CREATED',
  CRYPTO_BOUGHT = 'CRYPTO_BOUGHT',
  NEXUS_CALLBACK_HANDLED = 'NEXUS_CALLBACK_HANDLED',
  SENT_TO_PAGORA = 'SENT_TO_PAGORA',
  COMPLETED = 'COMPLETED',
}
export interface Filters {
  emailAddress?: string;
  referenceId?: string;
  euroAmount?: string;
  states?: TransactionRequestState[];
  purchasedAtGt?: Date;
  purchasedAtLt?: Date;
  brokerTransactionCode?: string;
  custodianTransactionCode?: string;
  blockchainTransactionId?: string;
}

export interface TransactionRequestHistory {
  createdAt: Date;
  state: TransactionRequestState;
}

export interface BrokerTransaction {
  createdAt: Date;
  transactionCode: string;
  accountCode: string;
  amount: MoneyScaled;
  euroAmount: number;
}

export enum TransactionType {
  SEND_INTERNAL = 'SEND_INTERNAL',
  SEND_EXTERNAL = 'SEND_EXTERNAL',
  RECEIVE_INTERNAL = 'RECEIVE_INTERNAL',
}

export interface CustodianUser {
  userId: string;
  emailAddress: string;
  status: CustodianUserStatus;
}

export enum CustodianUserStatus {
  ACTIVE = 'ACTIVE',
  BLOCKED = 'BLOCKED',
}

export interface CustodianTransaction {
  createdAt: Date;
  type: TransactionType;
  transactionCode: string;
  accountCode: string;
  amount: MoneyScaled;
  euroAmount: number;
  networkFee: number;
  blockchainTransactionId?: string;
}

export interface TransactionRequest {
  referenceId: string;
  emailAddress: string;
  euroAmount: number;
  purchasedAt: Date;
  createdAt: Date;
  state: TransactionRequestState;
  userId?: string;
}

export interface TransactionRequestDetails extends TransactionRequest {
  history: TransactionRequestHistory[];
  brokerTransaction?: BrokerTransaction;
  custodianTransactions: CustodianTransaction[];
}

export interface SearchTransactionsResponse {
  aggregatedData: {
    count: number;
  };
  transactionRequests: TransactionRequest[];
}

export interface PagoraQuickbitFilters {
  createdAtGt?: Date;
  createdAtLt?: Date;
  user?: 'pagora' | 'quickbit';
  types?: string[];
}
export interface PagoraQuickbitResponse {
  aggregatedData: {
    count: number;
    totalEuro: number;
    totalCrypto: number;
  };
  transactions: CustodianTransaction[];
}

type Pagination = { size: number; page: number };

export interface HotwalletBalancesResponse {
  currentPagoraBalance: number;
  currentQuickbitBalance: number;
  dcsHotwalletBalance: number;
  nexusBalances: {
    total: number;
    available: number;
    locked: number;
    unconfirmed: number;
  };
}

export interface UserFilters {
  emailAddress?: string;
  userId?: string;
  walletAddress?: string;
}

interface UsersSearchResponse {
  aggregatedData: {
    count: number;
  };
  users: CustodianUser[];
}

interface UserDetails {
  walletAddress: string;
  balance: number;
  euroBalance: number;
  userId: string;
  emailAddress: string;
  status: CustodianUserStatus;
  transactionRequests: string[];
}

export const useSearch = (filters: Filters, pagination: Pagination) => {
  return useQuery({
    queryKey: queryKeys.search(JSON.stringify({ ...filters, ...pagination })),
    queryFn: async () => {
      const convertedFilters = {
        ...filters,
        purchasedAtGt: filters.purchasedAtGt
          ? filters.purchasedAtGt.toISOString()
          : null,
        purchasedAtLt: filters.purchasedAtLt
          ? filters.purchasedAtLt.toISOString()
          : null,
      };
      const data = await fetchAPI<SearchTransactionsResponse>(
        `v1/direct-onramp/transaction-requests:search`,
        {
          method: 'POST',
          body: {
            pagination,
            filters: convertedFilters,
          },
        }
      );
      return {
        ...data,
        transactionRequests: data.transactionRequests.map((tr) => ({
          ...tr,
          purchasedAt: new Date(tr.purchasedAt),
        })),
      };
    },
    placeholderData: keepPreviousData,
  });
};

export const useDetails = (referenceId: string) => {
  return useQuery({
    queryKey: queryKeys.details(referenceId),
    queryFn: async () => {
      const data = await fetchAPI<TransactionRequestDetails>(
        `v1/direct-onramp/transaction-requests/${referenceId}/details`
      );
      return {
        ...data,
        purchasedAt: new Date(data.purchasedAt),
        createdAt: new Date(data.createdAt),
        history: data.history.map((h) => ({
          ...h,
          createdAt: new Date(h.createdAt),
        })),
        brokerTransaction: data.brokerTransaction
          ? {
              ...data.brokerTransaction,
              createdAt: new Date(data.brokerTransaction.createdAt),
            }
          : undefined,
        custodianTransactions: data.custodianTransactions.map((ct) => ({
          ...ct,
          createdAt: new Date(ct.createdAt),
        })),
      };
    },
  });
};

export const usePagoraQuickbitOverview = (
  filters: PagoraQuickbitFilters,
  pagination: Pagination
) => {
  return useQuery({
    queryKey: queryKeys.pagoraQuickbit(
      JSON.stringify({ ...filters, ...pagination })
    ),
    queryFn: async () => {
      const convertedFilters = {
        ...filters,
        createdAtGt: filters.createdAtGt
          ? filters.createdAtGt.toISOString()
          : null,
        createdAtLt: filters.createdAtLt
          ? filters.createdAtLt.toISOString()
          : null,
      };
      const data = await fetchAPI<PagoraQuickbitResponse>(
        `v1/direct-onramp/custodian-transactions/pagora-quickbit`,
        {
          method: 'POST',
          body: {
            pagination,
            filters: convertedFilters,
          },
        }
      );
      return {
        ...data,
        transactions: data.transactions.map((tx) => ({
          ...tx,
          createdAt: new Date(tx.createdAt),
        })),
      };
    },
    placeholderData: keepPreviousData,
  });
};

export const useRestart = (referenceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: queryKeys.restart(referenceId),
    mutationFn: async () => {
      await fetchAPI(
        `v1/direct-onramp/transaction-requests/${referenceId}:restart`,
        {
          method: 'POST',
        }
      );
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.details(referenceId),
      });
    },
  });
};

export const useBatchRestart = (referenceIds: string[]) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: queryKeys.restart(referenceIds.join(',')),
    mutationFn: async () => {
      const results = await Promise.all(
        referenceIds.map(async (referenceId) => {
          try {
            await fetchAPI(
              `v1/direct-onramp/transaction-requests/${referenceId}:restart`,
              {
                method: 'POST',
              }
            );
            return { [referenceId]: true };
          } catch {
            return { [referenceId]: false };
          }
        })
      );
      return Object.assign({}, ...results) as Record<string, boolean>;
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.allDetails(),
      });
    },
  });
};

export const useHotwalletBalances = () => {
  return useQuery({
    queryKey: queryKeys.hotWalletBalances(),
    queryFn: async () => {
      return await fetchAPI<HotwalletBalancesResponse>(
        `v1/direct-onramp/hotwallet-balances`
      );
    },
  });
};

const randomNumber = (max: number) => {
  return Math.floor(Math.random() * max) + 1;
};

const randomHexString = (length = 8) => {
  return Array.from({ length: length }, () =>
    Math.floor(Math.random() * 16).toString(16)
  ).join('');
};

export const useStartDirectOnrampFlow = (interval: number) => {
  return useQuery({
    queryKey: queryKeys.startDirectOnrampFlow(),
    queryFn: async () => {
      const customerId = 'customer-id-' + randomNumber(100).toString();
      const referenceId = randomHexString(24);
      const emailAddress = `qb-bo-${customerId}@quickbit.com`;
      const euroAmount = (randomNumber(20) + 19) / 100; // Random amount between 0.2 and 0.40 EUR
      return await fetchAPI<{ referenceId: string }>(
        `v1/direct-onramp/transaction-requests`,
        {
          method: 'POST',
          body: {
            emailAddress,
            euroAmount,
            purchasedAt: new Date().toISOString(),
            referenceId,
            callbackUrl: 'https://httpbin.org/post',
          },
        }
      );
    },
    refetchInterval: interval,
    enabled: interval > 0,
  });
};

export const useSearchUsers = (
  filters: UserFilters,
  pagination: Pagination
) => {
  return useQuery({
    queryKey: queryKeys.searchUsers(
      JSON.stringify({ ...filters, ...pagination })
    ),
    queryFn: async () => {
      return await fetchAPI<UsersSearchResponse>(
        `v1/direct-onramp/users:search`,
        {
          method: 'POST',
          body: {
            pagination,
            filters,
          },
        }
      );
    },
    placeholderData: keepPreviousData,
  });
};

export const useUserDetails = (userId: string) => {
  return useQuery({
    queryKey: queryKeys.detailsUsers(userId),
    queryFn: async () => {
      return await fetchAPI<UserDetails>(`v1/direct-onramp/users/${userId}`);
    },
  });
};

export const useUpdateUserStatus = (
  userId: string,
  status: CustodianUserStatus
) => {
  return useQuery({
    queryKey: queryKeys.updateUserStatus(userId, status),
    queryFn: async () => {
      return await fetchAPI<UserDetails>(
        `v1/direct-onramp/users/${userId}/status`,
        {
          method: 'POST',
          body: {
            status,
          },
        }
      );
    },
  });
};
